/**
 * Sticker outline extraction for cutting plotters.
 *
 * Pure geometry/image utilities (DOM is touched only to rasterize the
 * source image onto a canvas) so the geometry can be unit-tested directly.
 *
 * Pipeline: alpha mask (threshold) → boundary edge walk (clockwise outer
 * contours + counter-clockwise hole contours in screen coords) →
 * Douglas-Peucker simplification → polygon offset (miter-limited joins,
 * holes shrink so the margin stays uniform) → SVG path `d`.
 */

/**
 * Builds a binary solid/transparent mask from a loaded image (or canvas).
 * A pixel is "solid" when its alpha is >= threshold.
 * @param {HTMLImageElement|HTMLCanvasElement} image loaded image or canvas
 * @param {number} threshold 0-255 alpha threshold (default 128)
 * @returns {{ w: number, h: number, solid: Uint8Array, hasAlpha: boolean }}
 */
export const computeAlphaMask = (image, threshold = 128) => {
  const w = image.naturalWidth || image.width
  const h = image.naturalHeight || image.height
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.drawImage(image, 0, 0)
  const data = ctx.getImageData(0, 0, w, h).data
  const solid = new Uint8Array(w * h)
  let hasAlpha = false
  for (let i = 0; i < w * h; i++) {
    const a = data[i * 4 + 3]
    if (a < 255) hasAlpha = true
    solid[i] = a >= threshold ? 1 : 0
  }
  return { w, h, solid, hasAlpha }
}

/* Direction codes: 0=E, 1=S, 2=W, 3=N (screen coords: y grows downward). */
const DX = [1, 0, -1, 0]
const DY = [0, 1, 0, -1]

/**
 * Traces all boundary contours of a binary mask by following oriented
 * pixel edges (Potrace-style): every solid pixel emits the 4 directed
 * edges of its boundary with the solid kept on the RIGHT-hand side of
 * travel. That yields clockwise outer contours and counter-clockwise hole
 * contours (both needed: a plotter must cut interior holes too). At saddle
 * corners the walk prefers turning right, then straight, then left, which
 * keeps cycles simple and deterministic.
 * @param {Uint8Array} solid binary mask
 * @param {number} w mask width
 * @param {number} h mask height
 * @returns {number[][][]} array of contours (integer [x, y] corner points)
 */
export const marchingSquaresSquares = (solid, w, h) => {
  const solidAt = (x, y) => (x >= 0 && x < w && y >= 0 && y < h && solid[y * w + x] === 1)

  /* Directed boundary edges keyed by start corner: key = cy*(w+1)+cx,
   * value = array of direction codes leaving that corner. */
  const outgoing = new Map()
  const addEdge = (cx, cy, dir) => {
    const key = cy * (w + 1) + cx
    const list = outgoing.get(key)
    if (list) list.push(dir)
    else outgoing.set(key, [dir])
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!solidAt(x, y)) continue
      // Top edge (walk E), right edge (walk S), bottom (W), left (N):
      // each kept only when the pixel across the edge is not solid.
      if (!solidAt(x, y - 1)) addEdge(x, y, 0)
      if (!solidAt(x + 1, y)) addEdge(x + 1, y, 1)
      if (!solidAt(x, y + 1)) addEdge(x + 1, y + 1, 2)
      if (!solidAt(x - 1, y)) addEdge(x, y + 1, 3)
    }
  }

  const used = new Set() // edgeKey = (cy*(w+1)+cx)*4 + dir
  const edgeId = (cx, cy, dir) => (cy * (w + 1) + cx) * 4 + dir
  const contours = []
  const maxSteps = 4 * w * h + 8

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // Seed only on E-going top edges: every cycle contains at least one.
      const startId = edgeId(x, y, 0)
      if (!outgoing.get(y * (w + 1) + x)?.includes(0) || used.has(startId)) continue

      const contour = []
      let cx = x
      let cy = y
      let dir = 0
      let steps = 0
      // Terminate on returning to the start corner (position only): the
      // seed E edge is consumed up front, so any return means the cycle is
      // complete — also when a saddle sits on the start corner.
      do {
        used.add(edgeId(cx, cy, dir))
        contour.push([cx, cy])
        // Choose the next edge: prefer right turn, then straight, then left.
        const options = outgoing.get((cy + DY[dir]) * (w + 1) + (cx + DX[dir])) || []
        const preference = [(dir + 1) % 4, dir, (dir + 3) % 4]
        let nextDir = -1
        for (const cand of preference) {
          if (options.includes(cand) && !used.has(edgeId(cx + DX[dir], cy + DY[dir], cand))) {
            nextDir = cand
            break
          }
        }
        if (nextDir === -1) break // isolated edge (should not happen)
        cx += DX[dir]
        cy += DY[dir]
        dir = nextDir
        steps++
      } while ((cx !== x || cy !== y) && steps < maxSteps)
      if (contour.length >= 4) contours.push(contour)
    }
  }
  return contours
}

/**
 * Douglas-Peucker simplification for a polyline.
 * @param {number[][]} points
 * @param {number} tolerance max deviation in px (0 keeps only essential turns)
 * @returns {number[][]}
 */
export const simplifyContour = (points, tolerance = 1, closed = false) => {
  if (points.length <= 2) return points.slice()

  const sqTol = tolerance * tolerance
  const keep = new Array(points.length).fill(false)
  keep[0] = true
  keep[points.length - 1] = true

  const stack = [[0, points.length - 1]]
  while (stack.length) {
    const [first, last] = stack.pop()
    let maxSqDist = 0
    let index = -1
    const [x1, y1] = points[first]
    const [x2, y2] = points[last]
    const dx = x2 - x1
    const dy = y2 - y1
    const lenSq = dx * dx + dy * dy
    for (let i = first + 1; i < last; i++) {
      const [px, py] = points[i]
      let sqDist
      if (lenSq === 0) {
        const ex = px - x1
        const ey = py - y1
        sqDist = ex * ex + ey * ey
      } else {
        let t = ((px - x1) * dx + (py - y1) * dy) / lenSq
        t = Math.max(0, Math.min(1, t))
        const ex = px - (x1 + t * dx)
        const ey = py - (y1 + t * dy)
        sqDist = ex * ex + ey * ey
      }
      if (sqDist > maxSqDist) {
        maxSqDist = sqDist
        index = i
      }
    }
    if (maxSqDist > sqTol && index > 0) {
      keep[index] = true
      stack.push([first, index], [index, last])
    }
  }
  let out = points.filter((_, i) => keep[i])
  if (closed) {
    // DP anchors the seam chord (first→last edge); points that turned
    // collinear across the seam are cleaned here iteratively.
    out = removeCollinearPoints(out, tolerance)
  }
  return out
}

/**
 * Iteratively removes points whose deviation from the segment joining
 * their neighbours is within tolerance (handles the ring seam too).
 * @param {number[][]} points
 * @param {number} tolerance
 * @returns {number[][]}
 */
const removeCollinearPoints = (points, _tolerance) => {
  const sqEps = 1e-12 // exact collinearity only: DP already applied tolerance
  let pts = points.slice()
  let removed = true
  while (removed && pts.length > 3) {
    removed = false
    const keep = new Array(pts.length).fill(true)
    const n = pts.length
    for (let i = 0; i < n; i++) {
      const [px, py] = pts[i]
      const [ax, ay] = pts[(i - 1 + n) % n]
      const [bx, by] = pts[(i + 1) % n]
      const dx = bx - ax
      const dy = by - ay
      const lenSq = dx * dx + dy * dy
      let sqDist
      if (lenSq === 0) {
        const ex = px - ax
        const ey = py - ay
        sqDist = ex * ex + ey * ey
      } else {
        let t = ((px - ax) * dx + (py - ay) * dy) / lenSq
        t = Math.max(0, Math.min(1, t))
        const ex = px - (ax + t * dx)
        const ey = py - (ay + t * dy)
        sqDist = ex * ex + ey * ey
      }
      if (sqDist <= sqEps) {
        keep[i] = false
        removed = true
      }
    }
    pts = pts.filter((_, i) => keep[i])
  }
  return pts
}

/**
 * Removes consecutive duplicate points.
 * @param {number[][]} points
 * @returns {number[][]}
 */
const dedupePoints = (points) => {
  const out = []
  for (const p of points) {
    const last = out[out.length - 1]
    if (!last || last[0] !== p[0] || last[1] !== p[1]) out.push(p)
  }
  return out
}

/**
 * Offsets a closed clockwise (screen coords) polygon outward by delta px
 * with miter-limited joins. Consecutive offset points closer than
 * |delta|/3 are collapsed to reduce self-intersections on concave areas.
 * @param {number[][]} points closed contour (first point may equal last)
 * @param {number} delta offset in px (negative shrinks)
 * @returns {number[][]} offset open polyline
 */
export const offsetClosedPath = (points, delta) => {
  const src = dedupePoints(points.slice())
  if (src.length > 1) {
    const [fx, fy] = src[0]
    const [lx, ly] = src[src.length - 1]
    if (fx === lx && fy === ly) src.pop()
  }
  if (src.length < 3) return src
  if (!delta || Math.abs(delta) < 1e-9) return src

  const n = src.length
  const out = []
  const minSq = (Math.abs(delta) / 3) ** 2
  for (let i = 0; i < n; i++) {
    const [px, py] = src[i]
    const [ax, ay] = src[(i - 1 + n) % n]
    const [bx, by] = src[(i + 1) % n]
    // outward normals of the two incident edges (clockwise contour)
    let e1x = py - ay
    let e1y = -(px - ax)
    let e2x = by - py
    let e2y = -(bx - px)
    const l1 = Math.hypot(e1x, e1y)
    const l2 = Math.hypot(e2x, e2y)
    if (!l1 || !l2) continue
    e1x /= l1; e1y /= l1
    e2x /= l2; e2y /= l2
    let nx = e1x + e2x
    let ny = e1y + e2y
    const nl = Math.hypot(nx, ny)
    if (nl < 1e-9) { // straight reversal: reuse previous edge normal
      nx = e1x
      ny = e1y
    } else {
      nx /= nl
      ny /= nl
    }
    // miter scale: 1/cos(half turn angle), clamped to avoid spikes
    const cosHalf = Math.max(0.2, nx * e1x + ny * e1y)
    const scale = Math.min(3, 1 / cosHalf)
    const ox = px + nx * delta * scale
    const oy = py + ny * delta * scale
    const last = out[out.length - 1]
    if (last) {
      const ddx = ox - last[0]
      const ddy = oy - last[1]
      if (ddx * ddx + ddy * ddy < minSq) continue
    }
    out.push([ox, oy])
  }
  return out
}

/**
 * Converts a point list to an SVG path `d` string.
 * @param {number[][]} points
 * @param {boolean} closed
 * @returns {string}
 */
export const pathToD = (points, closed = true) => {
  if (!points.length) return ''
  const parts = [`M ${points[0][0]} ${points[0][1]}`]
  for (let i = 1; i < points.length; i++) {
    parts.push(`L ${points[i][0]} ${points[i][1]}`)
  }
  if (closed) parts.push('Z')
  return parts.join(' ')
}

/**
 * Full pipeline: loaded image → outline path data in image pixel coords.
 * @param {HTMLImageElement|HTMLCanvasElement} image loaded image
 * @param {object} opts
 * @param {number} [opts.threshold=128] alpha threshold 0-255
 * @param {number} [opts.offset=0] outward offset in image px
 * @param {number} [opts.tolerance=0] Douglas-Peucker tolerance in px
 *   (0 = exact axis-aligned corners; small values smooth pixel staircases)
 * @returns {{ d: string, count: number }} subpaths joined in one `d`
 */
export const traceImageOutline = (image, opts = {}) => {
  const { threshold = 128, offset = 0, tolerance = 0 } = opts
  const { w, h, solid } = computeAlphaMask(image, threshold)
  if (!w || !h) return { d: '', count: 0 }
  const contours = marchingSquaresSquares(solid, w, h)
  let count = 0
  const dParts = []
  for (const contour of contours) {
    const simplified = simplifyContour(contour, tolerance, true)
    if (simplified.length < 3) continue
    // Winding decides offset direction: outer contours are clockwise
    // (expand), holes are counter-clockwise (shrink) so a physical margin
    // stays uniform around every boundary.
    const signed = contourSignedArea(simplified)
    const delta = offset ? offset * (signed >= 0 ? 1 : -1) : 0
    const offsetPts = delta ? offsetClosedPath(simplified, delta) : simplified
    if (offsetPts.length < 3) continue
    dParts.push(pathToD(offsetPts, true))
    count++
  }
  return { d: dParts.join(' '), count }
}

/**
 * Signed area of a closed polygon (positive = clockwise in screen coords
 * with y down).
 * @param {number[][]} points closed ring (first may equal last)
 * @returns {number}
 */
const contourSignedArea = (points) => {
  let area = 0
  const n = points.length
  for (let i = 0; i < n; i++) {
    const [x1, y1] = points[i]
    const [x2, y2] = points[(i + 1) % n]
    area += x1 * y2 - x2 * y1
  }
  return area / 2
}
